import type { Theme } from '@mui/material/styles';
import { PRISM_NAVIGATION_PALETTE } from './prismDesignTokens';
import type { PrismNavigationPalette } from './prismTheme.types';

// Dark-mode variant of PRISM_NAVIGATION_PALETTE, covering only chrome surfaces (bars, tabs,
// menus, icon buttons). Saturated/branded tokens (status badges, the AI chip, the always-dark
// top bar) are left untouched — they already read fine on dark.
export const createDarkPrismNavigationPalette = (baseTheme: Theme): PrismNavigationPalette => {
  const surface = baseTheme.palette.background.paper;
  const textPrimary = baseTheme.palette.text.primary;
  const textSecondary = baseTheme.palette.text.secondary;
  const textDisabled = baseTheme.palette.text.disabled;
  const { divider } = baseTheme.palette;
  const hoverOverlay = baseTheme.palette.action.hover;
  const selectedOverlay = baseTheme.palette.action.selected;
  const brandMain = baseTheme.palette.primary.main;
  const brandLight = baseTheme.palette.primary.light;

  return {
    ...PRISM_NAVIGATION_PALETTE,
    primary: {
      ...PRISM_NAVIGATION_PALETTE.primary,
      menu: {
        ...PRISM_NAVIGATION_PALETTE.primary.menu,
        surface,
        search: {
          background: hoverOverlay,
          border: divider,
          text: textPrimary,
        },
        action: {
          background: surface,
          border: brandMain,
          hoverBackgroundBase: brandMain,
          text: brandLight,
        },
        item: {
          ...PRISM_NAVIGATION_PALETTE.primary.menu.item,
          activeBackground: selectedOverlay,
          disabled: textDisabled,
          header: textSecondary,
          hoverBackground: hoverOverlay,
          primaryText: textPrimary,
          secondaryText: textSecondary,
        },
      },
    },
    secondary: {
      ...PRISM_NAVIGATION_PALETTE.secondary,
      bar: { background: surface },
      title: { text: textPrimary },
      tab: {
        default: textSecondary,
        disabled: textDisabled,
        focusOutline: brandLight,
        hoverBackground: hoverOverlay,
        selectedBackground: selectedOverlay,
        selectedText: textPrimary,
      },
      infoLabel: {
        ...PRISM_NAVIGATION_PALETTE.secondary.infoLabel,
        background: surface,
        border: divider,
        label: textSecondary,
        value: {
          ...PRISM_NAVIGATION_PALETTE.secondary.infoLabel.value,
          default: textPrimary,
        },
      },
      actionButton: {
        ...PRISM_NAVIGATION_PALETTE.secondary.actionButton,
        disabledBackground: hoverOverlay,
        disabledBorder: divider,
        disabledText: textDisabled,
        ghost: {
          background: 'transparent',
          border: 'transparent',
          hoverBackground: hoverOverlay,
          hoverText: textPrimary,
          text: textSecondary,
        },
        secondary: {
          ...PRISM_NAVIGATION_PALETTE.secondary.actionButton.secondary,
          background: surface,
        },
      },
      iconButton: {
        activeBackground: selectedOverlay,
        activeText: textPrimary,
        containedBackground: hoverOverlay,
        containedHoverBackground: selectedOverlay,
        containedText: textSecondary,
        defaultText: textSecondary,
        disabledText: textDisabled,
        hoverBackground: hoverOverlay,
        hoverText: textPrimary,
      },
      selector: {
        ...PRISM_NAVIGATION_PALETTE.secondary.selector,
        background: surface,
        border: divider,
        disabledBackground: hoverOverlay,
        disabledBorder: divider,
        disabledText: textDisabled,
        filterBackground: hoverOverlay,
        filterHoverBackground: selectedOverlay,
        filterText: textSecondary,
        focusOutline: brandLight,
        hoverBackground: hoverOverlay,
        hoverBorder: brandLight,
        hoverText: brandLight,
        icon: textSecondary,
        label: textSecondary,
      },
    },
    tertiary: {
      ...PRISM_NAVIGATION_PALETTE.tertiary,
      bar: { background: surface },
      item: {
        defaultText: textSecondary,
        disabledText: textDisabled,
        focusOutline: brandLight,
        hoverBackground: hoverOverlay,
        hoverText: textPrimary,
        menuIcon: textSecondary,
        selectedBackground: selectedOverlay,
        selectedText: textPrimary,
      },
      iconButton: {
        background: surface,
        color: textSecondary,
        disabledColor: textDisabled,
        hoverBackground: hoverOverlay,
        hoverColor: textPrimary,
        menuBackground: 'transparent',
      },
      currencySelector: {
        ...PRISM_NAVIGATION_PALETTE.tertiary.currencySelector,
        background: surface,
        border: divider,
        hoverBackground: hoverOverlay,
        unitBackground: hoverOverlay,
        unitHoverBackground: selectedOverlay,
        unitText: textSecondary,
      },
    },
  };
};
